import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Send, MessageCircle, X, TrendingUp, Coins, Trophy, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatSidebar() {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineCount, setOnlineCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  // Fetch messages
  const fetchMessages = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/chat/messages`, {
        params: { channel: 'general', limit: 50 },
        headers
      });
      setMessages(response.data.messages || []);
      setOnlineCount(response.data.online_count || Math.floor(Math.random() * 50) + 10);
    } catch (error) {
      // Silently fail if not authenticated - user will see "login to chat" message
      if (error.response?.status !== 403 && error.response?.status !== 401) {
        console.error('Failed to fetch messages:', error);
      }
    }
  };

  useEffect(() => {
    fetchMessages();
    pollInterval.current = setInterval(fetchMessages, 3000);
    return () => clearInterval(pollInterval.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !token) return;

    try {
      await axios.post(
        `${API_URL}/api/chat/send`,
        { message: newMessage.trim(), channel: 'general' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get avatar URL - prefer Roblox avatar, fallback to dicebear
  const getAvatarUrl = (msg) => {
    if (msg.roblox_avatar) {
      return msg.roblox_avatar;
    }
    if (msg.roblox_user_id) {
      return `https://www.roblox.com/headshot-thumbnail/image?userId=${msg.roblox_user_id}&width=150&height=150&format=png`;
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${msg.username}`;
  };

  // Get display name - prefer Roblox username
  const getDisplayName = (msg) => {
    return msg.roblox_username || msg.username;
  };

  // Fetch user stats when clicking on avatar
  const handleAvatarClick = async (msg) => {
    setSelectedUser(msg);
    setLoadingStats(true);
    setUserStats(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/users/${msg.user_id}/stats`);
      setUserStats(response.data);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      setUserStats({ error: true });
    } finally {
      setLoadingStats(false);
    }
  };

  const closeStatsPopup = () => {
    setSelectedUser(null);
    setUserStats(null);
  };

  return (
    <div className="w-72 bg-[#0a0e14] border-r border-slate-800/50 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-blue-400" />
            <span className="text-white text-sm font-semibold">#general</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 px-2 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-green-400 text-xs font-medium">{onlineCount} online</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4 min-h-0 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        {messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">
            <MessageCircle size={32} className="mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
            <p className="text-xs mt-1">Be the first to say hello!</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className="group hover:bg-slate-800/30 rounded-lg p-2 -mx-2 transition-colors">
              <div className="flex items-start gap-3">
                {/* Roblox Avatar - Clickable for stats */}
                <button
                  onClick={() => handleAvatarClick(msg)}
                  className="cursor-pointer hover:opacity-80 transition-opacity focus:outline-none"
                  data-info="go fuck yourself"
                >
                  <img
                    src={getAvatarUrl(msg)}
                    alt={getDisplayName(msg)}
                    className="w-9 h-9 rounded-full bg-slate-700 flex-shrink-0 border-2 border-slate-700 hover:border-blue-500 transition-colors"
                    onError={(e) => {
                      e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${msg.username}`;
                    }}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <button
                      onClick={() => handleAvatarClick(msg)}
                      className={`text-sm font-semibold truncate hover:underline cursor-pointer text-left ${
                        msg.role === 'admin' ? 'text-red-400' :
                        msg.role === 'mod' ? 'text-green-400' :
                        msg.role === 'vip' ? 'text-yellow-400' : 'text-blue-400'
                      }`}
                    >
                      {getDisplayName(msg)}
                    </button>
                    <span className="text-[10px] text-slate-600 flex-shrink-0">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm break-words leading-relaxed mt-0.5">{msg.message}</p>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 border-t border-slate-800/50 bg-[#080b10]">
        {user ? (
          <form onSubmit={sendMessage} className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              maxLength={200}
              className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:bg-slate-800 transition-all"
            />
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className="p-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-xl transition-colors"
            >
              <Send size={18} className="text-white" />
            </button>
          </form>
        ) : (
          <div className="text-center py-3 bg-slate-800/30 rounded-xl">
            <p className="text-slate-400 text-sm">Please login to chat</p>
          </div>
        )}
      </div>

      {/* User Stats Popup */}
      {selectedUser && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeStatsPopup}>
          <div 
            className="bg-[#0a0e14] border border-slate-700/50 rounded-2xl w-72 max-w-[90%] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Popup Header */}
            <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={getAvatarUrl(selectedUser)}
                  alt={getDisplayName(selectedUser)}
                  className="w-10 h-10 rounded-full border-2 border-blue-500"
                  onError={(e) => {
                    e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedUser.username}`;
                  }}
                />
                <div>
                  <p className={`font-semibold ${
                    selectedUser.role === 'admin' ? 'text-red-400' :
                    selectedUser.role === 'mod' ? 'text-green-400' :
                    selectedUser.role === 'vip' ? 'text-yellow-400' : 'text-white'
                  }`}>
                    {getDisplayName(selectedUser)}
                  </p>
                  {selectedUser.roblox_user_id && (
                    <p className="text-slate-500 text-xs">@{selectedUser.username}</p>
                  )}
                </div>
              </div>
              <button
                onClick={closeStatsPopup}
                className="p-1 hover:bg-slate-700/50 rounded-lg transition-colors"
              >
                <X size={18} className="text-slate-400" />
              </button>
            </div>

            {/* Stats Content */}
            <div className="p-4">
              {loadingStats ? (
                <div className="text-center py-6">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-slate-500 text-sm mt-3">Loading stats...</p>
                </div>
              ) : userStats?.error ? (
                <div className="text-center py-6">
                  <p className="text-slate-500 text-sm">Unable to load stats</p>
                </div>
              ) : userStats ? (
                <div className="space-y-3">
                  {/* Games Played */}
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Trophy size={16} className="text-yellow-400" />
                      <span className="text-slate-400 text-sm">Games Played</span>
                    </div>
                    <span className="text-white font-semibold">{userStats.total_games || 0}</span>
                  </div>

                  {/* Total Wagered */}
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Coins size={16} className="text-blue-400" />
                      <span className="text-slate-400 text-sm">Total Wagered</span>
                    </div>
                    <span className="text-white font-semibold">{(userStats.total_wagered || 0).toLocaleString()} R$</span>
                  </div>

                  {/* Win Rate */}
                  <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-xl">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={16} className="text-green-400" />
                      <span className="text-slate-400 text-sm">Win Rate</span>
                    </div>
                    <span className={`font-semibold ${
                      (userStats.win_rate || 0) >= 50 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {(userStats.win_rate || 0).toFixed(1)}%
                    </span>
                  </div>

                  {/* Wins / Losses */}
                  <div className="flex gap-2">
                    <div className="flex-1 p-3 bg-green-500/10 rounded-xl text-center">
                      <p className="text-green-400 font-bold text-lg">{userStats.wins || 0}</p>
                      <p className="text-slate-500 text-xs">Wins</p>
                    </div>
                    <div className="flex-1 p-3 bg-red-500/10 rounded-xl text-center">
                      <p className="text-red-400 font-bold text-lg">{userStats.losses || 0}</p>
                      <p className="text-slate-500 text-xs">Losses</p>
                    </div>
                  </div>

                  {/* Roblox Profile Link */}
                  {selectedUser.roblox_user_id && (
                    <a
                      href={`https://www.roblox.com/users/${selectedUser.roblox_user_id}/profile`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-xl text-sm font-medium transition-colors"
                    >
                      <ExternalLink size={14} />
                      View Roblox Profile
                    </a>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
